import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useFilters } from '../../hooks/useFilters'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { FILTER_TYPES } from '../../constants/filters'
import StatDisplay from '../../components/common/StatDisplay'
import { formatRemainingTime, formatTime } from '../../utils/date.util'
import UserTypeDisplay from '../../components/common/UserTypeDisplay'
import DisplayActiveSuspended from '../../components/admin/DisplayActiveSuspended'
import EditSubscriptionTierModal from '../../components/admin/EditSubscriptionTierModal'
import { useState } from 'react'
import DisplayShopName from '../../components/common/DisplayShopName'

const AdminSubscriptionsPage = () => {
  const { filters, renderFilters, renderPagination } = useFilters()

  const { data: statsData } = useQuery({
    queryKey: QueryKeys.adminSubscriptionStats(),
    queryFn: () => handleGetRequest('/admin/subscription-stats'),
  })

  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery({
    queryKey: QueryKeys.adminListSubscriptions(filters),
    queryFn: () => handleGetRequest('/admin/subscriptions', filters),
  })

  const stats = statsData?.data || {}
  const subscriptions = subscriptionsData?.data || []
  const [addModalOpen, setAddModalOpen] = useState(false)

  const renderSummary = () => {
    return (
      <div className="flex gap-6 mb-8">
        <StatDisplay label="Active Subscriptions" value={stats.totalActiveSubscriptions} />
        <StatDisplay label="Expired Subscriptions" value={stats.expiredSubscriptions} />
        <StatDisplay label="Upcoming Renewals" value={stats.upcomingRenewals} />
        <StatDisplay label="Revenue This Month" value={stats.revenueThisMonthText} />
      </div>
    )
  }

  return (
    <>
      {renderSummary()}
      <div className="w-full flex mb-2 gap-4">
        {renderFilters([FILTER_TYPES.search])}
        <button
          className="bg-secondary-600 text-white rounded px-4 py-2 font-medium shadow hover:opacity-90 transition"
          type="button"
          onClick={() => setAddModalOpen(true)}
        >
          Edit Subscription Tiers
        </button>
      </div>
      <div className="flex items-center mb-8 w-full gap-4 ">
        {renderFilters([
          FILTER_TYPES.subscriptionTier,
          FILTER_TYPES.userType,
          FILTER_TYPES.isActive,
          FILTER_TYPES.date,
        ])}
      </div>
      <div className="border border-gray-300 rounded-md overflow-hidden bg-background">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-[#F4DEC2] [&>th]:px-6 [&>th]:py-4 [&>th]:font-medium [&>th]:text-black [&>th]:text-base">
              <th>Shop</th>
              <th>Phone</th>
              <th>Subscription Tier</th>
              <th>Remaining Duration</th>
              <th>Created</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {subscriptionsLoading ? (
              <tr>
                <td colSpan="6" className="text-center text-gray-400 py-8">
                  Loading subscriptions...
                </td>
              </tr>
            ) : subscriptions.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-gray-400 py-8">
                  No subscriptions found.
                </td>
              </tr>
            ) : (
              subscriptions.map((row) => (
                <tr
                  key={row.id}
                  className={`border-t border-gray-200 last:border-b-0 [&>td]:px-6 [&>td]:py-4`}
                >
                  <td>
                    <DisplayShopName
                      shop={{
                        name: row.shopName,
                        isPremium: row.isShopPremium,
                        isVerified: row.isShopApproved,
                      }}
                    />
                    <div className="mt-1 text-gray-600 text-sm font-semibold">
                      <span>{row.userName}</span> - <UserTypeDisplay userType={row.userType} />
                    </div>
                  </td>
                  <td>{row.phoneNumber}</td>
                  <td>{row.subscriptionTier}</td>
                  <td>{formatRemainingTime(row.expiresAt)}</td>
                  <td>{formatTime(row.createdAt)}</td>
                  <td>
                    <DisplayActiveSuspended isActive={row.isActive} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <EditSubscriptionTierModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} />
      </div>
      {renderPagination(subscriptionsData?.meta)}
    </>
  )
}

export default AdminSubscriptionsPage
